import { fetchMe, updateMeProfile, type UpdateMeInput, type UserProfile } from './appsync-client'
import { patchSetupProfile } from '../stores/setup-profile-store'
import {
  getRecordingPolicyConsent,
  hasAcceptedCurrentRecordingPolicy,
  hydrateRecordingPolicyConsentFromProfile,
  readCachedRecordingPolicyConsent,
  type RecordingPolicyConsentState,
} from '../stores/recording-policy-store'

/**
 * Durable recording-policy consent (Members / Independents): one acceptance
 * covers making AND receiving calls; a decline blocks both directions until
 * accepted (also enforced server-side by the call INVITE gate).
 *
 * All reads/writes go through the `me` / `updateMe` service layer — the
 * decision timestamp and notice version are stamped server-side.
 */

/** Load the current decision from the profile (also hydrates the store). */
export async function loadRecordingPolicyConsent(): Promise<RecordingPolicyConsentState> {
  const profile = await fetchMe()
  hydrateRecordingPolicyConsentFromProfile(profile)
  return getRecordingPolicyConsent()
}

/** Ensure the store is hydrated (no-op when already loaded this session). */
export async function ensureRecordingPolicyConsentLoaded(): Promise<RecordingPolicyConsentState> {
  const current = getRecordingPolicyConsent()
  if (current.loaded) return current
  return loadRecordingPolicyConsent()
}

export type ResolvedRecordingPolicyConsent = {
  /** Accepted the current policy version. */
  accepted: boolean
  /** Undecided (or accepted an older version): the policy should be presented. */
  undecided: boolean
  acceptedAt: string | null
  noticeVersion: string | null
  source: 'server' | 'cache' | 'unknown'
}

const DEFAULT_SERVER_WAIT_MS = 2_500

/**
 * The account's recording-policy decision for a "should we prompt?" check.
 * Asks the server (bounded wait so an incoming call is never stalled); only if
 * that fails falls back to the last server-confirmed answer cached for this
 * account. Before this, the check read an in-memory store that was empty on
 * every cold start whose launch profile read failed or hadn't finished yet —
 * the first launch after an app update — so accepted users were asked again.
 */
export async function resolveRecordingPolicyConsent(
  userId: string | null | undefined,
  options?: { timeoutMs?: number },
): Promise<ResolvedRecordingPolicyConsent> {
  const fromState = (
    state: Pick<RecordingPolicyConsentState, 'status' | 'acceptedAt' | 'noticeVersion'>,
    source: ResolvedRecordingPolicyConsent['source'],
  ): ResolvedRecordingPolicyConsent => {
    const accepted = hasAcceptedCurrentRecordingPolicy(state)
    return {
      accepted,
      undecided: !accepted && state.status !== 'DECLINED',
      acceptedAt: state.acceptedAt,
      noticeVersion: state.noticeVersion,
      source,
    }
  }
  const current = getRecordingPolicyConsent()
  if (current.loaded) return fromState(current, 'server')
  const timeoutMs = options?.timeoutMs ?? DEFAULT_SERVER_WAIT_MS
  let timer: ReturnType<typeof setTimeout> | undefined
  const loaded = await Promise.race([
    ensureRecordingPolicyConsentLoaded().catch(() => null),
    new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), timeoutMs)
    }),
  ])
  if (timer) clearTimeout(timer)
  if (loaded?.loaded) return fromState(loaded, 'server')
  const cached = readCachedRecordingPolicyConsent(userId)
  if (cached) return fromState(cached, 'cache')
  return {
    accepted: false,
    undecided: true,
    acceptedAt: null,
    noticeVersion: null,
    source: 'unknown',
  }
}

export type RecordingPolicyDecisionContext = NonNullable<UpdateMeInput['recordingPolicyConsentContext']>

async function recordDecision(
  accepted: boolean,
  context?: RecordingPolicyDecisionContext,
): Promise<UserProfile> {
  const profile = await updateMeProfile({
    recordingPolicyConsent: accepted,
    ...(context ? { recordingPolicyConsentContext: context } : {}),
  })
  hydrateRecordingPolicyConsentFromProfile(profile)
  // Answering clears a pending "asked again" request (the server re-stamps the time).
  patchSetupProfile(profile.userId, {
    recordingPolicyConsentStatus: profile.recordingPolicyConsentStatus,
    recordingPolicyConsentAt: profile.recordingPolicyConsentAt,
    recordingPolicyConsentNoticeVersion: profile.recordingPolicyConsentNoticeVersion,
  })
  return profile
}

/**
 * Accept the recording policy — enables calling in both directions. Pass where
 * it was shown (the Easy Read wording); omit for the original notice.
 */
export async function acceptRecordingPolicy(
  context?: RecordingPolicyDecisionContext,
): Promise<UserProfile> {
  return recordDecision(true, context)
}

/** Decline the recording policy — blocks calling both directions until accepted. */
export async function declineRecordingPolicy(
  context?: RecordingPolicyDecisionContext,
): Promise<UserProfile> {
  return recordDecision(false, context)
}
